import type { Signal } from '@angular/core';
import { computed } from '@angular/core';
import type { FieldTree } from '@angular/forms/signals';

/**
 * Minimal envelope shape a versioned widget entity must satisfy to
 * install {@link widgetEnvelopePlugin}. Schema twin in entity-core:
 * `widgetBaseSchema` (`widget/common/schemas/base.ts`).
 */
export type WidgetEnvelopeData = {
    id: string;
    parentId: string;
    entityType: `widget:${string}`;
    entitySubType: 'default' | 'zero';
    version: number;
    // Optional too: hosts compiled without `strictNullChecks` infer the nullable key as optional.
    meta: { displayId?: string | null };
};

export type WidgetEnvelopeApi<E extends WidgetEnvelopeData = WidgetEnvelopeData> = {
    /** The placement variant — `'zero'` for a zero-block widget, the versioned counterpart of the legacy `type` tag. */
    readonly entitySubType: FieldTree<E['entitySubType']>;
    /**
     * Stable element id for DOM attributes — the user's `meta.displayId` if set,
     * otherwise `widget-${id}`.
     */
    displayId: Signal<string>;
    setDisplayId(id: string): void;
};

/**
 * Envelope accessors shared by every versioned widget form — the counterpart of
 * `legacyWidgetEnvelopePlugin`. `id` / `parentId` already come from the versioned base
 * form. Receives the root FieldTree lazily (`() => _raw`) and exposes getters,
 * so compose it with `composeForm`, never object spread.
 */
export function widgetEnvelopePlugin<E extends WidgetEnvelopeData>(root: () => FieldTree<E>): WidgetEnvelopeApi<E> {
    // FieldTree is invariant (writable signals), so the narrowing is done via an
    // explicit cast — safe by the `E extends WidgetEnvelopeData` constraint.
    const r = root as unknown as () => FieldTree<WidgetEnvelopeData>;
    return {
        get entitySubType() {
            return r().entitySubType as FieldTree<E['entitySubType']>;
        },
        displayId: computed(() => {
            const value = r()().value();
            return value.meta?.displayId || `widget-${value.id ?? ''}`;
        }),
        // `meta` is required on every versioned widget schema, so the child node always exists —
        // unlike the legacy optional root `displayId`, no write-through-root is needed.
        setDisplayId(id: string) {
            r().meta.displayId().value.set(id);
        },
    };
}
